import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, User, Lock, Mail, CheckCircle2 } from "lucide-react";

export default function Profile() {
    const { user, refreshUser } = useAuth();
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", new_password: "", current_password: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [ok, setOk] = useState(false);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setOk(false); setBusy(true);
        try {
            const payload = { current_password: form.current_password };
            if (form.name && form.name !== user?.name) payload.name = form.name;
            if (form.email && form.email !== user?.email) payload.email = form.email;
            if (form.new_password) payload.new_password = form.new_password;
            if (Object.keys(payload).length === 1) {
                setErr("Asnjë ndryshim për të ruajtur"); setBusy(false); return;
            }
            await api.patch("/auth/me", payload);
            setOk(true);
            setForm({ ...form, new_password: "", current_password: "" });
            if (refreshUser) await refreshUser();
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally { setBusy(false); }
    };

    const homeFor = (role) => role === "super_admin" ? "/admin" : role === "teacher" ? "/teacher" : role === "student" ? "/student" : role === "secretary" ? "/secretary" : role === "quality_coordinator" ? "/quality" : "/parent";

    return (
        <Layout>
            <Link to={homeFor(user?.role)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="max-w-2xl">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Profili im</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{user?.name}</h1>
                <p className="text-slate-600 mt-1 text-sm">{user?.email} • <span className="font-bold uppercase tracking-wider">{user?.role === "parent" ? "Prind" : user?.role === "super_admin" ? "Super Admin" : user?.role === "student" ? "Nxënës" : user?.role === "secretary" ? "Sekretar" : user?.role === "quality_coordinator" ? "Koordinator i Cilësisë" : "Mësues"}</span></p>

                <form onSubmit={submit} className="mt-8 space-y-5 bg-white border border-slate-200 rounded-2xl p-6">
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-2"><User className="w-3.5 h-3.5" /> Emri</label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-2"><Mail className="w-3.5 h-3.5" /> Email</label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="profile-email" />
                    </div>
                    <div>
                        <label className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5 mb-2"><Lock className="w-3.5 h-3.5" /> Fjalëkalim i ri (opsional)</label>
                        <Input type="password" placeholder="Lëre bosh nëse nuk dëshiron ta ndryshosh" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="profile-new-password" />
                    </div>
                    <div className="border-t border-slate-200 pt-5">
                        <label className="text-xs font-bold uppercase tracking-widest text-terracotta flex items-center gap-1.5 mb-2"><Lock className="w-3.5 h-3.5" /> Fjalëkalimi aktual (i nevojshëm për ruajtje)</label>
                        <Input type="password" required value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="profile-current-password" />
                    </div>
                    {err && <div className="text-sm text-terracotta bg-terracotta/10 border border-terracotta/30 rounded-lg px-4 py-2" data-testid="profile-error">{err}</div>}
                    {ok && <div className="text-sm text-sage bg-sage/10 border border-sage/30 rounded-lg px-4 py-2 inline-flex items-center gap-2" data-testid="profile-ok"><CheckCircle2 className="w-4 h-4" /> Profili u ruajt me sukses</div>}
                    <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white" data-testid="profile-submit">
                        {busy ? "Duke ruajtur…" : "Ruaj ndryshimet"}
                    </Button>
                </form>
            </div>
        </Layout>
    );
}
